<template>
  <div class="table-wrapper">
    <statement-details v-if="getterPopupName === 'ImportTxnDetails'"
              :txnDetails="popupDetails" @refreshList="refreshTransaction" />
    <statement-filter v-else-if="getterPopupName === 'ImportTxnFilter'"
              @updateFilters="updateFilters" />
    <statement-export v-else-if="getterPopupName === 'ImportTxnExport'"
              :exportOptions="paginate" :exportRecords="exportRecords" />

    <div class="table-title">
      <div class="subject">
        <p>Transactions</p>
        <span v-if="typeof paginate.total === 'number'">
          Total ({{paginate.total}})
        </span>
      </div>

      <div class="filters">
        <div class="options">
          <button @click="openPopup('ImportTxnExport')">
            Export
            <i class="fa fa-share" />
          </button>
          <button @click="openPopup('ImportTxnFilter')"
                  :class="{'filters-applied': filtersApplied}"
                  class="dark-btn">
            Filter
            <i class="fa fa-filter" />
          </button>
        </div>

        <statement-search placeholder="Search ID / Ref number / Bank ref number"
                  @populateList="clearSearch" @setFunctions="setSearchFunctions"
                  :triggerSearch="triggerSearch" :searchTerm="searchTerm" />
      </div>
    </div>

    <div class="table-head" v-if="isDesktop()">
      <p>ID</p>
      <p>Created Time</p>
      <p>Payment Type</p>
      <p>Amount</p>
      <p>Reference Number</p>
      <p>Bank Reference Number</p>
      <p>Status</p>
    </div>

    <div class="table-body">
      <div class="table-row cursor"
        v-for="(data, index) in list" :key="index"
        @click="showDetails(data)"
      >
        <p class="mt-grey-text">
          <b v-if="!isDesktop()">ID:</b>
          <span>{{data.id}}</span>
        </p>
        <p class="mt-grey-text mt-right">
          {{data.created_at}}
        </p>
        <p>
          <b v-if="!isDesktop()">Pymt Type:</b>
          {{data.payment_type}}
        </p>
        <p class="mt-right">
          <b v-if="!isDesktop()">Amount:</b>
          {{amountFormat(data.amount)}}
        </p>
        <p>
          <b v-if="!isDesktop()">Ref Number:</b>
          <span>{{data.reference_number}}</span>
        </p>
        <p class="mt-right">
          <b v-if="!isDesktop()">Bank Ref Number:</b>
          <span>{{data.bank_ref_num}}</span>
        </p>
        <p class="mt-grey-text">
          {{data.status}}
          <i class="fa fa-sync-alt" v-if="data.status === 'PENDING'"
              @click="refreshTransaction($event, data)" />
        </p>
      </div>
    </div>

    <div class="table-empty" v-if="paginate.total === 0">
      <p v-if="searchTerm">Unable to find transactions with <span class="highlight">{{searchTerm}}</span>.<br>Please search with the complete ID / Reference Number / Bank Reference Number.<br>Search is case sensitive - please search with the correct case to get the valid results.</p>
      <p v-else>{{emptyRecords}}</p>
    </div>

    <table-loader v-if="!paginate.data_added || list.length < paginate.total"
                  :css="{'single-side': !isDesktop()}" />
  </div>
</template>

<script>
import StatementExport from '@/components/global/ExportConfirmation'
import StatementSearch from '@/components/global/SearchRecords'
import StatementFilter from '@/components/Imports/StatementFilter'
import StatementDetails from '@/components/AccountStatement/StatementDetails'

import ApiServices from '@/services/api/imports'
import listMixins from '@/mixins/listMixins'
import { amountFormat } from '@/utils/helpers'
import { mapGetters } from 'vuex'

export default {
  name: 'ImportTransactions',
  components: { StatementFilter, StatementSearch, StatementExport, StatementDetails },
  mixins: [listMixins],
  data () {
    return {
      importId: null
    }
  },
  created () {
    this.importId = this.$route.params.id
    this.startProcessing()
  },
  watch: {
    getterImportStatus () {
      this.resetData()
      this.populateData(0)
    }
  },
  computed: {
    ...mapGetters(['getterSectionDetails', 'getterImportStatus'])
  },
  methods: {
    amountFormat,
    populateParams () {
      this.api['search'] = ApiServices.searchTransactions
      this.api['export'] = ApiServices.exportTransactions
      this.keys['list'] = 'transactions'
      this.keys['popup'] = 'ImportTxnDetails'
      const params = { import_id: this.importId }
      this.params['search'] = params
      this.params['export'] = params
      this.setHighlightSelectors([1, 5, 6])
    },
    fetchRecords (start) {
      return new Promise(resolve => {
        let params = {...this.filterOptions}
        params['import_id'] = this.importId
        if (start) {
          params['offset'] = start
          params['filter_token'] = this.paginate['filter_token']
        } else {
          params['fetch_import_details'] = !this.getterSectionDetails
        }

        ApiServices.transactions(params).then(response => {
          this.SET_STATEMENT_FILTER(this.filterOptions)
          let data = response.data
          if (data) {
            if (start) {
              this.list.push(...data.transactions)
            } else {
              this.list = data.transactions
              this.setPaginate(data)
              this.populateMeta(response)
            }
            resolve()
          }
        }).catch((error) => {
          if (error.status === 404) {
            this.$router.push({name: 'ImportsList'})
          }
        })
      })
    },
    populateMeta ({data}) {
      if (data.import) {
        this.SET_SECTION_DETAILS(data.import)
      }
    },
    refreshTransaction (event, {id}, fromDetailsPopup) {
      event.stopPropagation()
      ApiServices.transactionStatus(id, {import_id: this.importId}).then(response => {
        let data = response.data
        if (data) {
          const txnIndex = this.list.findIndex(elem => elem.id === id)
          const txn = data.transaction
          this.list.splice(txnIndex, 1, txn)

          if (data.import) {
            this.SET_SECTION_DETAILS(data.import)
          }

          if (fromDetailsPopup) {
            this.popupDetails = txn
          }
        } else if (response.reason === 'UNPRIVILEGED') {
          this.errorToastMsg(response.message)
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
  @import '@/assets/scss/table.scss';

  .mobile, .tablet {
    .table-row {
      p {
        white-space: unset !important;
      }
    }
  }
</style>
